import { useEffect, useState } from 'react';
import { CheckboxState, CheckboxTreeNodeType } from './CheckboxTree.types';
import {
	calculateParentState,
	computeValuesWithCheckedAncestors,
	fromFormState,
	toFormState,
	transformDataToNodes,
} from './CheckboxTree.helpers';
import { CheckboxTreeNode } from './CheckboxTreeNode';
import { GeoNode } from 'domain/subscription/interfaces/Subscription';

export const AreaCheckboxTree: React.FC<{
	initialState?: string[];
	availableAreas: GeoNode[];
	onChange: (selectedAreas: string[]) => void;
}> = ({ initialState, availableAreas, onChange }) => {
	const [nodes, setNodes] = useState<CheckboxTreeNodeType[]>(
		fromFormState(transformDataToNodes(availableAreas), initialState || []),
	);

	useEffect(() => {
		const payload = toFormState(nodes);
		onChange(payload);
	}, [nodes]);

	const updateNodeChecked = (nodeValue: string, newState: CheckboxState) => {
		const collectNodeAndDescendantValues = (
			node: CheckboxTreeNodeType,
		): string[] => {
			const descendantValues =
				node.children?.flatMap(collectNodeAndDescendantValues) ?? [];
			return [node.value, ...descendantValues];
		};

		const collectValuesToUpdate = (
			node: CheckboxTreeNodeType,
			values: Set<string>,
		): void => {
			if (node.value === nodeValue) {
				collectNodeAndDescendantValues(node).forEach((val) =>
					values.add(val),
				);
			}
			node.children?.forEach((child) =>
				collectValuesToUpdate(child, values),
			);
		};

		const valuesToUpdate = new Set<string>();
		nodes.forEach((node) => collectValuesToUpdate(node, valuesToUpdate));

		const applyUpdates = (
			node: CheckboxTreeNodeType,
		): CheckboxTreeNodeType => {
			const shouldUpdate = valuesToUpdate.has(node.value);
			const updatedChildren = node.children?.map(applyUpdates);

			if (shouldUpdate) {
				return {
					...node,
					state: newState,
					children: updatedChildren,
				};
			}

			if (updatedChildren) {
				return {
					...node,
					state: calculateParentState(updatedChildren),
					children: updatedChildren,
				};
			}

			return node;
		};

		setNodes((prevNodes) => prevNodes.map(applyUpdates));
	};

	const valuesWithCheckedAncestors = computeValuesWithCheckedAncestors(nodes);

	return (
		<>
			{nodes.map((node) => (
				<CheckboxTreeNode
					key={node.value}
					value={node.value}
					label={node.label}
					state={node.state}
					descendants={node.children}
					onToggle={updateNodeChecked}
					valuesWithCheckedAncestors={valuesWithCheckedAncestors}
				/>
			))}
		</>
	);
};
